import React, { useState, useEffect } from 'react';
import { 
    getProducts, updateProduct, 
    getPlansByProduct, createPlan, updatePlan, deletePlan,
    getFeaturesByPlan, createFeature, updateFeature, deleteFeature
} from '../../services/adminService';
import { ChevronDown, ChevronRight, Plus, Edit, Trash2, Save, X, Check } from 'lucide-react';

const ProductsManager = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedProduct, setExpandedProduct] = useState(null);
    const [expandedPlan, setExpandedPlan] = useState(null);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const data = await getProducts();
            setProducts(data);
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleProduct = (productId) => {
        if (expandedProduct === productId) {
            setExpandedProduct(null);
            setExpandedPlan(null);
        } else {
            setExpandedProduct(productId);
            setExpandedPlan(null);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Cargando productos...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">Gestión de Productos</h2>
            </div>

            <div className="grid gap-6">
                {products.map(product => (
                    <ProductCard 
                        key={product.ProductoId} 
                        product={product} 
                        isExpanded={expandedProduct === product.ProductoId}
                        onToggle={() => toggleProduct(product.ProductoId)}
                        expandedPlan={expandedPlan}
                        setExpandedPlan={setExpandedPlan}
                        onUpdate={loadProducts}
                    />
                ))}
            </div>
        </div>
    );
};

const ProductCard = ({ product, isExpanded, onToggle, expandedPlan, setExpandedPlan, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...product });
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);

    useEffect(() => {
        if (isExpanded) {
            loadPlans();
        }
    }, [isExpanded]);

    const loadPlans = async () => {
        setLoadingPlans(true);
        try {
            const data = await getPlansByProduct(product.ProductoId);
            setPlans(data);
        } catch (error) {
            console.error("Error loading plans", error);
        } finally {
            setLoadingPlans(false);
        }
    };

    const handleSave = async () => {
        try {
            await updateProduct(product.ProductoId, formData);
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            alert("Error al actualizar producto: " + error.message);
        }
    };

    return (
        <div className={`bg-white rounded-xl shadow-sm border transition-all duration-200 ${isExpanded ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'}`}>
            {/* Product Header */}
            <div className="p-6 flex items-start justify-between gap-4">
                <div className="flex-1">
                    {isEditing ? (
                        <div className="space-y-4">
                            <input 
                                className="w-full text-xl font-bold text-slate-900 border-b border-slate-300 focus:border-blue-500 outline-none pb-1"
                                value={formData.Nombre}
                                onChange={e => setFormData({...formData, Nombre: e.target.value})}
                                placeholder="Nombre del Producto"
                            />
                            <input 
                                className="w-full text-sm text-slate-600 border-b border-slate-300 focus:border-blue-500 outline-none pb-1"
                                value={formData.Titulo}
                                onChange={e => setFormData({...formData, Titulo: e.target.value})}
                                placeholder="Título Corto"
                            />
                            <textarea 
                                className="w-full text-sm text-slate-500 border rounded p-2 focus:border-blue-500 outline-none"
                                value={formData.Descripcion}
                                onChange={e => setFormData({...formData, Descripcion: e.target.value})}
                                placeholder="Descripción"
                                rows={2}
                            />
                        </div>
                    ) : (
                        <div onClick={onToggle} className="cursor-pointer">
                            <div className="flex items-center gap-2 mb-1">
                                {isExpanded ? <ChevronDown size={20} className="text-blue-500" /> : <ChevronRight size={20} className="text-slate-400" />}
                                <h3 className="text-xl font-bold text-slate-800">{product.Nombre}</h3>
                            </div>
                            <p className="text-sm text-slate-500 ml-7">{product.Titulo}</p>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} className="p-2 text-green-600 hover:bg-green-50 rounded-full"><Check size={20} /></button>
                            <button onClick={() => setIsEditing(false)} className="p-2 text-red-600 hover:bg-red-50 rounded-full"><X size={20} /></button>
                        </>
                    ) : (
                        <button onClick={() => setIsEditing(true)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"><Edit size={18} /></button>
                    )}
                </div>
            </div>

            {/* Plans Section */}
            {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50 p-6 rounded-b-xl">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-slate-700">Planes de Suscripción</h4>
                        <button 
                            onClick={() => {
                                // Logic to add new plan
                                const newPlan = { ProductoId: product.ProductoId, Plan: 'Nuevo Plan', Precio: 0, Recomendado: false, HorasPorMes: 0 };
                                createPlan(newPlan).then(loadPlans);
                            }}
                            className="text-sm flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                        >
                            <Plus size={16} /> Agregar Plan
                        </button>
                    </div>

                    {loadingPlans ? (
                        <div className="text-sm text-slate-500">Cargando planes...</div>
                    ) : (
                        <div className="space-y-4">
                            {plans.map(plan => (
                                <PlanCard 
                                    key={plan.PlanId} 
                                    plan={plan} 
                                    isExpanded={expandedPlan === plan.PlanId}
                                    onToggle={() => setExpandedPlan(expandedPlan === plan.PlanId ? null : plan.PlanId)}
                                    onUpdate={loadPlans}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const PlanCard = ({ plan, isExpanded, onToggle, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...plan });
    const [features, setFeatures] = useState([]);
    const [loadingFeatures, setLoadingFeatures] = useState(false);

    useEffect(() => {
        if (isExpanded) {
            loadFeatures();
        }
    }, [isExpanded]);

    const loadFeatures = async () => {
        setLoadingFeatures(true);
        try {
            const data = await getFeaturesByPlan(plan.PlanId);
            setFeatures(data);
        } catch (error) {
            console.error("Error loading features", error);
        } finally {
            setLoadingFeatures(false);
        }
    };

    const handleSave = async () => {
        try {
            await updatePlan(plan.PlanId, formData);
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            alert("Error al actualizar plan: " + error.message);
        }
    };

    const handleDelete = async () => {
        if (window.confirm("¿Eliminar este plan?")) {
            try {
                await deletePlan(plan.PlanId);
                onUpdate();
            } catch (error) {
                alert("Error: " + error.message);
            }
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1">
                    {isEditing ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                            <input 
                                className="font-semibold text-slate-800 border-b border-slate-300 focus:border-blue-500 outline-none"
                                value={formData.Plan}
                                onChange={e => setFormData({...formData, Plan: e.target.value})}
                                placeholder="Nombre del Plan"
                            />
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500">$</span>
                                <input 
                                    type="number"
                                    className="w-32 border-b border-slate-300 focus:border-blue-500 outline-none"
                                    value={formData.Precio}
                                    onChange={e => setFormData({...formData, Precio: e.target.value})}
                                    placeholder="Precio"
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-500">Horas:</span>
                                    <input 
                                        type="number"
                                        className="w-16 border-b border-slate-300 focus:border-blue-500 outline-none"
                                        value={formData.HorasPorMes}
                                        onChange={e => setFormData({...formData, HorasPorMes: e.target.value})}
                                    />
                                </div>
                                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={formData.Recomendado} 
                                        onChange={e => setFormData({...formData, Recomendado: e.target.checked})}
                                    />
                                    Recomendado
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div onClick={onToggle} className="cursor-pointer grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                            <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown size={16} className="text-blue-500" /> : <ChevronRight size={16} className="text-slate-400" />}
                                <span className="font-semibold text-slate-800">{plan.Plan}</span>
                                {plan.Recomendado && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Recomendado</span>}
                            </div>
                            <div className="text-slate-600 font-medium">
                                $ {Number(plan.Precio).toLocaleString('es-AR')}
                            </div>
                            <div className="text-sm text-slate-500">
                                {plan.HorasPorMes} horas/mes
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check size={18} /></button>
                            <button onClick={() => setIsEditing(false)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><X size={18} /></button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setIsEditing(true)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit size={16} /></button>
                            <button onClick={handleDelete} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                        </>
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="bg-slate-50 p-4 border-t border-slate-200 pl-12">
                    <div className="flex justify-between items-center mb-2">
                        <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Features</h5>
                        <button 
                            onClick={() => {
                                createFeature({ ProductoId: plan.ProductoId, PlanId: plan.PlanId, Feature: 'Nueva Feature' }).then(loadFeatures);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                            <Plus size={12} /> Agregar
                        </button>
                    </div>
                    <div className="space-y-2">
                        {features.map(feature => (
                            <FeatureRow key={feature.FeatureId} feature={feature} onUpdate={loadFeatures} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const FeatureRow = ({ feature, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(feature.Feature);

    const handleSave = async () => {
        try {
            await updateFeature(feature.FeatureId, { Feature: text });
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async () => {
        if (window.confirm("¿Eliminar feature?")) {
            try {
                await deleteFeature(feature.FeatureId);
                onUpdate();
            } catch (error) {
                console.error(error);
            }
        }
    };

    if (isEditing) {
        return (
            <div className="flex items-center gap-2">
                <input 
                    className="flex-1 text-sm border rounded px-2 py-1 focus:border-blue-500 outline-none"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    autoFocus
                />
                <button onClick={handleSave} className="text-green-600 hover:bg-green-50 p-1 rounded"><Check size={14} /></button>
                <button onClick={() => setIsEditing(false)} className="text-red-600 hover:bg-red-50 p-1 rounded"><X size={14} /></button>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between group hover:bg-white p-1 rounded transition-colors">
            <span className="text-sm text-slate-600 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                {feature.Feature}
            </span>
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
                <button onClick={() => setIsEditing(true)} className="text-slate-400 hover:text-blue-600 p-1"><Edit size={12} /></button>
                <button onClick={handleDelete} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={12} /></button>
            </div>
        </div>
    );
};

export default ProductsManager;
